import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import { useAuthStore } from '@/store/authStore';
import { formatAppointmentWhen } from '@/utils/format-appointment';
import { useIconChip } from '@/hooks/use-theme-colors';

const DEFAULT_BRAND = '#FD5001';

/**
 * Confirmação da assinatura/recorrência pedida pelo convidado no perfil
 * público. Irmã de `[appointmentId]/sucesso`: o pedido nasce aguardando o
 * prestador e não tem um agendamento único com token, então a rota é própria
 * e leva à lista de agendamentos. Oferece a conta pelo caminho.
 */
export default function GuestRecurrenceSentScreen() {
  const chip = useIconChip();
  const { subscription, sessions, first, service, slug, brand, claimable } = useLocalSearchParams<{
    subscription?: string;
    sessions?: string;
    first?: string;
    service?: string;
    slug?: string;
    brand?: string;
    claimable?: string;
  }>();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const brandColor = typeof brand === 'string' && brand ? brand : DEFAULT_BRAND;
  const isSubscription = subscription === '1';
  const total = Number(sessions) || 0;
  const sessionsLabel = total === 1 ? '1 sessão' : `${total} sessões`;
  const canCreateAccount = !isAuthenticated && claimable !== '0';

  return (
    <View className="flex-1 bg-neutral-50">
      <PublicBrandHeader />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1 bg-neutral-50"
        contentContainerStyle={{ flexGrow: 1, padding: 20, gap: 20 }}
      >
        <View
          className="items-center gap-3 rounded-3xl bg-card p-6"
          style={{ borderCurve: 'continuous' }}
        >
          <View
            className="h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: chip(brandColor, '1A').bg }}
          >
            <Ionicons name="hourglass-outline" size={34} color={chip(brandColor, '1A').fg} />
          </View>
          <ThemedText className="text-center font-bold text-2xl">
            {isSubscription ? 'Assinatura solicitada!' : 'Recorrência solicitada!'}
          </ThemedText>
          {!!service && (
            <ThemedText className="text-center font-semibold text-base text-neutral-700">
              {service}
            </ThemedText>
          )}
          {total > 0 && (
            <ThemedText className="text-center text-base text-neutral-600">
              {sessionsLabel} reservadas
            </ThemedText>
          )}
          {!!first && (
            <ThemedText className="text-center text-sm text-neutral-500">
              Primeira: {formatAppointmentWhen(first)}
            </ThemedText>
          )}
          <ThemedText className="text-center text-sm text-neutral-500">
            Os horários ficam guardados para você enquanto o prestador confirma. Você recebe a
            confirmação pelo WhatsApp.
          </ThemedText>
        </View>

        {canCreateAccount && (
          <View className="gap-3 rounded-3xl bg-card p-6" style={{ borderCurve: 'continuous' }}>
            <View className="flex-row items-center gap-3">
              <View
                className="h-10 w-10 items-center justify-center rounded-2xl"
                style={{ backgroundColor: chip(brandColor, '1A').bg, borderCurve: 'continuous' }}
              >
                <Ionicons name="sparkles-outline" size={20} color={chip(brandColor, '1A').fg} />
              </View>
              <ThemedText className="flex-1 font-bold text-lg">
                Acompanhe pela Controlapag
              </ThemedText>
            </View>
            <ThemedText className="text-sm leading-5 text-neutral-500">
              Crie sua conta grátis com o mesmo WhatsApp para ver suas sessões, remarcar e pagar em
              um só lugar.
            </ThemedText>
            <PressableScale
              haptic
              onPress={() =>
                router.push({
                  pathname: '/agendamento/conta',
                  params: { brand: brandColor, ...(slug ? { slug } : {}) },
                } as never)
              }
              className="mt-1 w-full flex-row items-center justify-center gap-2 rounded-full py-4"
              style={{ backgroundColor: brandColor, borderCurve: 'continuous' }}
            >
              <ThemedText className="font-bold text-base text-white">Criar minha conta</ThemedText>
            </PressableScale>
          </View>
        )}

        <View className="gap-2">
          <PressableScale
            onPress={() => router.replace('/agendamento' as never)}
            className="w-full items-center rounded-full border border-neutral-200 bg-card py-4"
            style={{ borderCurve: 'continuous' }}
          >
            <ThemedText className="font-bold text-neutral-700">Ver meus agendamentos</ThemedText>
          </PressableScale>

          {!!slug && (
            <Pressable
              onPress={() => router.replace(`/@${slug}` as never)}
              className="items-center py-2"
            >
              <ThemedText className="font-semibold text-sm text-neutral-500">
                Voltar ao perfil
              </ThemedText>
            </Pressable>
          )}
        </View>

        <View className="flex-1" />
        <View style={{ marginHorizontal: -20, marginTop: 24 }}>
          <PublicBrandFooter />
        </View>
      </ScrollView>
    </View>
  );
}
